import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const ViewMTC = () => {
  const [filters, setFilters] = useState({
    tc_no: '',
    heat_no: '',
    mill_code: '',
    from_date: '',
    to_date: ''
  });

  // Dialog states
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: mtcData, isLoading, error, refetch } = useQuery(
    ['viewMTC', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/raw-material/mtc?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching MTC data: ${error.message}`, { variant: 'error' });
      }
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setOpenViewDialog(true);
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setOpenEditDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenViewDialog(false);
    setOpenEditDialog(false);
    setSelectedRecord(null);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading MTC data: {error.message}
        </Alert>
      </Box>
    );
  }

  const mtcRecords = mtcData?.data?.mtc || [];

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        View Mill Test Certificates (MTC)
      </Typography>

      {/* Search Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="TC No"
              value={filters.tc_no}
              onChange={(e) => setFilters({ ...filters, tc_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Heat No"
              value={filters.heat_no}
              onChange={(e) => setFilters({ ...filters, heat_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Mill Code"
              value={filters.mill_code}
              onChange={(e) => setFilters({ ...filters, mill_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={() => refetch()}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {mtcRecords.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Certificates
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {mtcRecords.filter(r => r.status === 'approved').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Approved
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {mtcRecords.filter(r => r.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pending Review
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.main">
                    {new Set(mtcRecords.map(r => r.heat_no)).size}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Unique Heats
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* MTC Data Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Mill Test Certificates
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {mtcRecords.length} certificates found
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>MTC No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Mill Code</TableCell>
                    <TableCell>Steel Code</TableCell>
                    <TableCell>Heat No</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Carbon %</TableCell>
                    <TableCell>Manganese %</TableCell>
                    <TableCell>Silicon %</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mtcRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No MTC records found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mtcRecords.map((record, index) => (
                      <TableRow key={record.mtc_id || index}>
                        <TableCell>{record.tc_no}</TableCell>
                        <TableCell>{record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>{record.mill_code}</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>{record.heat_no}</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>{record.carbon ? parseFloat(record.carbon).toFixed(3) : '-'}</TableCell>
                        <TableCell>{record.manganese ? parseFloat(record.manganese).toFixed(3) : '-'}</TableCell>
                        <TableCell>{record.silicon ? parseFloat(record.silicon).toFixed(3) : '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={record.status || 'N/A'} 
                            color={getStatusColor(record.status)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleView(record)}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => handleEdit(record)}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Mill Test Certificate Details</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">TC Number</Typography>
                <Typography variant="body1">{selectedRecord.tc_no || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Heat Number</Typography>
                <Typography variant="body1">{selectedRecord.heat_no || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Mill Code</Typography>
                <Typography variant="body1">{selectedRecord.mill_code || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Date</Typography>
                <Typography variant="body1">{selectedRecord.createdAt ? new Date(selectedRecord.createdAt).toLocaleDateString() : '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Chemical Composition (%)</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Carbon</Typography>
                <Typography variant="body1">{selectedRecord.carbon ? parseFloat(selectedRecord.carbon).toFixed(3) : '-'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Manganese</Typography>
                <Typography variant="body1">{selectedRecord.manganese ? parseFloat(selectedRecord.manganese).toFixed(3) : '-'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Silicon</Typography>
                <Typography variant="body1">{selectedRecord.silicon ? parseFloat(selectedRecord.silicon).toFixed(3) : '-'}</Typography>
              </Grid>
              {selectedRecord.phosphorus && (
                <Grid item xs={4}>
                  <Typography variant="caption" color="textSecondary">Phosphorus</Typography>
                  <Typography variant="body1">{parseFloat(selectedRecord.phosphorus).toFixed(3)}</Typography>
                </Grid>
              )}
              {selectedRecord.sulphur && (
                <Grid item xs={4}>
                  <Typography variant="caption" color="textSecondary">Sulphur</Typography>
                  <Typography variant="body1">{parseFloat(selectedRecord.sulphur).toFixed(3)}</Typography>
                </Grid>
              )}
              {selectedRecord.chromium && (
                <Grid item xs={4}>
                  <Typography variant="caption" color="textSecondary">Chromium</Typography>
                  <Typography variant="body1">{parseFloat(selectedRecord.chromium).toFixed(3)}</Typography>
                </Grid>
              )}
              {(selectedRecord.tensile_strength || selectedRecord.yield_strength) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>Mechanical Properties</Typography>
                  </Grid>
                  {selectedRecord.tensile_strength && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Tensile Strength</Typography>
                      <Typography variant="body1">{selectedRecord.tensile_strength} MPa</Typography>
                    </Grid>
                  )}
                  {selectedRecord.yield_strength && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">Yield Strength</Typography>
                      <Typography variant="body1">{selectedRecord.yield_strength} MPa</Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Close</Button>
          <Button 
            onClick={() => {
              handleCloseDialogs();
              handleEdit(selectedRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs} maxWidth="md" fullWidth>
        <DialogTitle>Edit Mill Test Certificate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Edit functionality will be implemented in a future version.
          </Typography>
          {selectedRecord && (
            <Box>
              <Typography variant="body1">
                Editing MTC: {selectedRecord.tc_no} - {selectedRecord.heat_no}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button variant="contained" disabled>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewMTC;